<template>
  <div class="fi-map">
    <div ref="mapEl" class="fi-map__canvas" role="application" aria-label="Mapa da rota de entrega"></div>
    <p v-if="!hasPoints" class="fi-map__empty">Nenhum galpão com coordenada para exibir.</p>
  </div>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

const STATE_COLOR = {
  critico: '#c62828',
  atencao: '#ef6c00',
  folga: '#2e7d32',
  sem_consumo: '#9e9e9e',
  sem_leitura: '#9e9e9e',
}

const STATE_TEXT = {
  critico: 'crítico',
  atencao: 'atenção',
  folga: 'folga',
  sem_consumo: 'sem consumo',
  sem_leitura: 'sem leitura',
}

export default {
  name: 'FeedInventoryRouteMap',
  props: {
    origin: {
      default: null,
    },
    originLabel: {
      type: String,
      default: 'Sede',
    },
    markers: {
      type: Array,
      default: () => [],
    },
    trips: {
      type: Array,
      default: () => [],
    },
    selectedTrip: {
      default: null,
    },
  },
  data() {
    return {
      map: null,
      markerLayer: null,
      routeLayer: null,
    }
  },
  computed: {
    hasPoints() {
      return this.markers.length > 0 || (this.origin && this.origin.length === 2)
    },
  },
  watch: {
    markers: {
      deep: true,
      handler() {
        this.redraw(true)
      },
    },
    origin() {
      this.redraw(true)
    },
    trips: {
      deep: true,
      handler() {
        this.redraw(false)
      },
    },
    selectedTrip() {
      this.redraw(false)
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initMap()
      this.redraw(true)
      window.setTimeout(() => this.invalidateSize(), 200)
    })
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    initMap() {
      if (!this.$refs.mapEl || this.map) return
      this.map = L.map(this.$refs.mapEl, {
        scrollWheelZoom: true,
      })
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 18,
      }).addTo(this.map)
      L.control.scale({ imperial: false, metric: true }).addTo(this.map)
      this.routeLayer = L.layerGroup().addTo(this.map)
      this.markerLayer = L.layerGroup().addTo(this.map)
      this.map.setView([-26.5, -52.2], 7)
    },
    invalidateSize() {
      if (this.map) this.map.invalidateSize()
    },
    escapeHtml(text) {
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },
    circleIcon(color) {
      return L.divIcon({
        className: 'fi-map-icon',
        html: `<span style="display:block;width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)"></span>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      })
    },
    originIcon() {
      return L.divIcon({
        className: 'fi-map-icon',
        html: '<span style="display:block;width:14px;height:14px;border-radius:2px;background:#1a237e;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35)"></span>',
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      })
    },
    redraw(fit) {
      if (!this.map || !this.markerLayer || !this.routeLayer) return
      this.routeLayer.clearLayers()
      this.markerLayer.clearLayers()
      const bounds = []

      this.trips.forEach((trip, index) => {
        if (!trip.positions || trip.positions.length < 2) return
        const dim = this.selectedTrip != null && this.selectedTrip !== index
        const line = L.polyline(trip.positions, {
          color: trip.color,
          weight: this.selectedTrip === index ? 5 : 3,
          opacity: dim ? 0.2 : 0.9,
          dashArray: trip.full ? '8 6' : null,
        })
        line.on('click', () => this.$emit('select', index))
        line.addTo(this.routeLayer)
      })

      this.markers.forEach((marker) => {
        const lat = Number(marker.lat)
        const lng = Number(marker.lng)
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
        const countLabel = marker.count > 1 ? ` (${marker.count} galpões)` : ''
        const place = marker.municipality ? ` · ${marker.municipality}` : ''
        const state = STATE_TEXT[marker.state] || ''
        const pin = L.marker([lat, lng], {
          icon: this.circleIcon(STATE_COLOR[marker.state] || '#9e9e9e'),
        })
        pin.bindTooltip(
          `<strong>${this.escapeHtml(marker.name)}${countLabel}</strong>${this.escapeHtml(place)}${state ? `<br/>${this.escapeHtml(state)}` : ''}`,
          { direction: 'top', offset: [0, -8] }
        )
        pin.addTo(this.markerLayer)
        bounds.push([lat, lng])
      })

      if (this.origin && this.origin.length === 2) {
        const lat = Number(this.origin[0])
        const lng = Number(this.origin[1])
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          const pin = L.marker([lat, lng], { icon: this.originIcon(), zIndexOffset: 500 })
          pin.bindTooltip(`<strong>${this.escapeHtml(this.originLabel)}</strong>`, {
            direction: 'top',
            offset: [0, -8],
          })
          pin.addTo(this.markerLayer)
          bounds.push([lat, lng])
        }
      }

      if (fit) this.fit(bounds)
      this.$nextTick(() => this.invalidateSize())
    },
    fit(bounds) {
      if (!this.map) return
      if (!bounds.length) {
        this.map.setView([-26.5, -52.2], 7)
        return
      }
      if (bounds.length === 1) {
        this.map.setView(bounds[0], 13)
        return
      }
      this.map.fitBounds(L.latLngBounds(bounds), { padding: [36, 36], maxZoom: 13 })
    },
  },
}
</script>

<style scoped>
.fi-map {
  position: relative;
  min-height: 420px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background: #eef2f4;
}

.fi-map__canvas {
  height: 420px;
  width: 100%;
}

.fi-map__empty {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  margin: 0;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.92);
  color: #6c757d;
  font-size: 0.85rem;
}
</style>

<style>
.fi-map-icon {
  background: transparent;
  border: 0;
}
</style>
